import React, { useState, useEffect } from "react";
import {
  Save,
  Eye,
  EyeOff,
  RefreshCw,
  RotateCcw,
  Upload,
  Link as LinkIcon,
  Type,
  Image,
  Video,
} from "lucide-react";

const FeatureSectionManager = () => {
  const [bannerData, setBannerData] = useState({
    subTitle: "",
    mainTitle: "",
    highlightText: "",
    description: "",
    buttonText: "",
    buttonLink: "",
    youtubeUrl: "",
    backgroundImage: "",
    isActive: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [stats, setStats] = useState(null);

  const API_BASE_URL = "https://thumbnailgenerateproject02-e84afca2c852.herokuapp.com";

  useEffect(() => {
    fetchBannerData();
    fetchStats();
  }, []);

  const fetchBannerData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/banner-section`);
      const result = await response.json();

      if (result.success) {
        setBannerData(result.data);
      } else {
        showMessage("error", result.message || "Failed to fetch banner data");
      }
    } catch (error) {
      console.error("Error fetching banner data:", error);
      showMessage("error", "Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/banner-section/stats`);
      const result = await response.json();

      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/banner-section`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bannerData),
      });

      const result = await response.json();

      if (result.success) {
        setBannerData(result.data);
        showMessage("success", "Banner section updated successfully!");
        fetchStats();
      } else {
        showMessage(
          "error",
          result.message || "Failed to update banner section"
        );
      }
    } catch (error) {
      console.error("Error saving banner data:", error);
      showMessage("error", "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/banner-section/toggle`,
        {
          method: "PATCH",
        }
      );

      const result = await response.json();

      if (result.success) {
        setBannerData(result.data);
        showMessage("success", result.message);
        fetchStats();
      } else {
        showMessage(
          "error",
          result.message || "Failed to toggle banner status"
        );
      }
    } catch (error) {
      console.error("Error toggling banner status:", error);
      showMessage("error", "Failed to toggle banner status");
    }
  };

  const handleReset = async () => {
    if (
      window.confirm(
        "Are you sure you want to reset the banner section to default? This action cannot be undone."
      )
    ) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/banner-section/reset`,
          {
            method: "POST",
          }
        );

        const result = await response.json();

        if (result.success) {
          setBannerData(result.data);
          showMessage(
            "success",
            "Banner section reset to default successfully!"
          );
          fetchStats();
        } else {
          showMessage(
            "error",
            result.message || "Failed to reset banner section"
          );
        }
      } catch (error) {
        console.error("Error resetting banner:", error);
        showMessage("error", "Failed to reset banner section");
      }
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const handleInputChange = (field, value) => {
    setBannerData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateYouTubeUrl = (url) => {
    if (!url) return true;
    const youtubeRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/).+/;
    return youtubeRegex.test(url);
  };

  const validateImageUrl = (url) => {
    if (!url) return true;
    const imageRegex = /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)$/i;
    return imageRegex.test(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-800">
            Banner Section Management
          </h2>
          <div className="flex gap-2">
            <button
              onClick={handleToggleActive}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                bannerData.isActive
                  ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                  : "bg-green-500 hover:bg-green-600 text-white"
              }`}
            >
              {bannerData.isActive ? <EyeOff size={16} /> : <Eye size={16} />}
              {bannerData.isActive ? "Deactivate" : "Activate"}
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
            >
              <RotateCcw size={16} />
              Reset to Default
            </button>
          </div>
        </div>

        {message.text && (
          <div
            className={`p-4 rounded-md mb-4 ${
              message.type === "success"
                ? "bg-green-100 border border-green-400 text-green-700"
                : "bg-red-100 border border-red-400 text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm text-blue-600 font-medium">Status</div>
              <div
                className={`text-lg font-bold ${
                  stats.isActive ? "text-green-600" : "text-red-600"
                }`}
              >
                {stats.isActive ? "Active" : "Inactive"}
              </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-sm text-purple-600 font-medium">Video</div>
              <div
                className={`text-lg font-bold ${
                  stats.hasYoutubeVideo ? "text-green-600" : "text-gray-400"
                }`}
              >
                {stats.hasYoutubeVideo ? "Added" : "None"}
              </div>
            </div>
            <div className="bg-pink-50 p-3 rounded-lg">
              <div className="text-sm text-pink-600 font-medium">
                Background
              </div>
              <div
                className={`text-lg font-bold ${
                  stats.hasBackgroundImage ? "text-green-600" : "text-gray-400"
                }`}
              >
                {stats.hasBackgroundImage ? "Added" : "None"}
              </div>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="text-sm text-orange-600 font-medium">
                Last Updated
              </div>
              <div className="text-sm font-bold text-gray-700">
                {new Date(stats.lastUpdated).toLocaleDateString()}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Basic Text Content */}
        <div className="border rounded-lg p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
            <Type size={20} />
            Text Content
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sub Title
              </label>
              <input
                type="text"
                value={bannerData.subTitle}
                onChange={(e) => handleInputChange("subTitle", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Best AI Image Generator"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Highlight Text
              </label>
              <input
                type="text"
                value={bannerData.highlightText}
                onChange={(e) =>
                  handleInputChange("highlightText", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Art or Image"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Main Title *
              </label>
              <input
                type="text"
                value={bannerData.mainTitle}
                onChange={(e) => handleInputChange("mainTitle", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="What if AI could make any piece of"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={bannerData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                placeholder="Enter banner description. Use line breaks for new paragraphs."
              />
              <p className="text-sm text-gray-500 mt-1">
                Use line breaks to separate paragraphs
              </p>
            </div>
          </div>
        </div>

        {/* Button Configuration */}
        <div className="border rounded-lg p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
            <LinkIcon size={20} />
            Button Configuration
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Button Text
              </label>
              <input
                type="text"
                value={bannerData.buttonText}
                onChange={(e) =>
                  handleInputChange("buttonText", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Start Creating"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Button Link
              </label>
              <input
                type="text"
                value={bannerData.buttonLink}
                onChange={(e) =>
                  handleInputChange("buttonLink", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="/create"
              />
            </div>
          </div>
        </div>

        {/* Media Configuration */}
        <div className="border rounded-lg p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
            <Video size={20} />
            Media Configuration
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                YouTube Video URL
              </label>
              <input
                type="url"
                value={bannerData.youtubeUrl}
                onChange={(e) =>
                  handleInputChange("youtubeUrl", e.target.value)
                }
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  bannerData.youtubeUrl &&
                  !validateYouTubeUrl(bannerData.youtubeUrl)
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              {bannerData.youtubeUrl &&
                !validateYouTubeUrl(bannerData.youtubeUrl) && (
                  <p className="text-red-500 text-sm mt-1">
                    Please enter a valid YouTube URL
                  </p>
                )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Image URL
              </label>
              <input
                type="url"
                value={bannerData.backgroundImage}
                onChange={(e) =>
                  handleInputChange("backgroundImage", e.target.value)
                }
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  bannerData.backgroundImage &&
                  !validateImageUrl(bannerData.backgroundImage)
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
                placeholder="https://example.com/image.jpg"
              />
              {bannerData.backgroundImage &&
                !validateImageUrl(bannerData.backgroundImage) && (
                  <p className="text-red-500 text-sm mt-1">
                    Please enter a valid image URL (jpg, jpeg, png, gif, webp)
                  </p>
                )}
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Preview</h3>
          <div className="bg-gray-50 p-4 rounded-md">
            <div className="text-sm text-blue-600 mb-2">
              {bannerData.subTitle}
            </div>
            <div className="text-xl font-bold text-gray-800 mb-2">
              {bannerData.mainTitle}
              {bannerData.highlightText && (
                <span className="text-blue-600">
                  {" "}
                  {bannerData.highlightText}
                </span>
              )}
              {bannerData.highlightText && " in seconds?"}
            </div>
            <div className="text-gray-600 mb-4 whitespace-pre-line">
              {bannerData.description}
            </div>
            {bannerData.buttonText && (
              <div className="inline-block bg-blue-600 text-white px-4 py-2 rounded-md">
                {bannerData.buttonText}
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !bannerData.mainTitle}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md transition-colors font-medium"
          >
            {saving ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeatureSectionManager;
